/**
 * NumaPick — "The Anchor" (Bento top-left quadrant)
 *
 * Featured post of the day — the highest-engagement post from recent highlights.
 * Large card with prominent author, body preview, and "Read Deeply" CTA.
 */

import type { ClubTheme } from './themes';
import type { SocialPost } from './types';
import { useNumaPick } from './queries';

interface NumaPickProps {
  clubId: string;
  theme: ClubTheme;
  onPostClick?: (post: SocialPost) => void;
}

export default function NumaPick({ clubId, theme, onPostClick }: NumaPickProps) {
  const { data: post, isLoading } = useNumaPick(clubId);

  // Loading skeleton
  if (isLoading) {
    return (
      <div
        className="flex h-full flex-col rounded-2xl p-5"
        style={{
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.muted}15`,
        }}
      >
        <div className="mb-3 flex items-center gap-2">
          <div
            className="h-5 w-5 rounded-full"
            style={{ backgroundColor: theme.colors.primary }}
          />
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.colors.primary }}>
            Numa's Pick
          </span>
        </div>
        <div className="flex-1 space-y-3">
          <div className="h-4 w-3/4 animate-pulse rounded" style={{ backgroundColor: `${theme.colors.muted}15` }} />
          <div className="h-3 w-full animate-pulse rounded" style={{ backgroundColor: `${theme.colors.muted}10` }} />
          <div className="h-3 w-5/6 animate-pulse rounded" style={{ backgroundColor: `${theme.colors.muted}10` }} />
        </div>
      </div>
    );
  }

  // Empty state — no highlights today
  if (!post) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center rounded-2xl p-5 text-center"
        style={{
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.muted}15`,
        }}
      >
        <div className="mb-2 flex items-center gap-2">
          <div
            className="h-5 w-5 rounded-full"
            style={{ backgroundColor: theme.colors.primary }}
          />
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.colors.primary }}>
            Numa's Pick
          </span>
        </div>
        <p className="text-sm" style={{ color: theme.colors.muted }}>
          No featured post today. Check back soon.
        </p>
      </div>
    );
  }

  const authorName = post.author?.full_name || 'Unknown';
  const avatarUrl = post.author?.avatar_url;
  const bodyPreview = (post.body || '').slice(0, 200);

  return (
    <div
      className="flex h-full flex-col rounded-2xl p-5 transition-shadow hover:shadow-lg cursor-pointer"
      style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.primary}25`,
      }}
      onClick={() => onPostClick?.(post)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onPostClick?.(post)}
    >
      {/* Badge */}
      <div className="mb-3 flex items-center gap-2">
        <div
          className="h-5 w-5 rounded-full flex items-center justify-center text-[10px]"
          style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
        >
          N
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.colors.primary }}>
          Numa's Pick
        </span>
      </div>

      {/* Author */}
      <div className="flex items-center gap-2.5 mb-3">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold"
            style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
          >
            {(authorName[0] || '?').toUpperCase()}
          </div>
        )}
        <div>
          <span className="text-sm font-semibold" style={{ color: theme.colors.text }}>
            {authorName}
          </span>
          {post.channel && (
            <span className="ml-2 text-[10px]" style={{ color: theme.colors.muted }}>
              in {post.channel.name}
            </span>
          )}
        </div>
      </div>

      {/* Body preview */}
      <p
        className="flex-1 text-sm leading-relaxed line-clamp-4"
        style={{ color: theme.colors.text }}
      >
        {bodyPreview}{bodyPreview.length >= 200 ? '...' : ''}
      </p>

      {/* Engagement stats + CTA */}
      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-3 text-[11px]" style={{ color: theme.colors.muted }}>
          {Object.values(post.reactions || {}).reduce((a, b) => a + b, 0) > 0 && (
            <span>{Object.values(post.reactions).reduce((a, b) => a + b, 0)} reactions</span>
          )}
          {post.comment_count > 0 && <span>{post.comment_count} comments</span>}
        </div>
        <span
          className="text-[11px] font-semibold"
          style={{ color: theme.colors.primary }}
        >
          Read Deeply &rarr;
        </span>
      </div>
    </div>
  );
}
